import { zodResolver } from '@hookform/resolvers/zod';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { enqueueSnackbar } from '../../../../components/GlobalSnackbar/snackbarStore';
import { isSupabaseError } from '../../../../constants/supabaseErrors';
import { useSpace } from '../../../../hooks/useSpace';
import { DocumentType } from '../../../../types';
import useRenameFileInGoogleDrive from '../../api/useRenameFileInGoogleDrive';
import useUpdateDocument from '../../api/useUpdateDocument';
import { useGetDocumentTypes } from '../../api/useGetDocumentTypes';

export type EditableDocument = {
  id: string;
  name: string;
  type: DocumentType | null;
  documentTypeId?: string | null;
  fileUrl: string;
  providerId?: string | null;
  providerName?: string | null;
};

type Props = {
  document: EditableDocument;
  onClose: () => void;
};

const formSchema = z.object({
  name: z.string().trim().min(1, 'Le nom est obligatoire'),
  type: z.custom<DocumentType>().nullable(),
  provider_id: z.string().trim().nullable(),
  provider_name: z.string().trim(),
});

export default function useEditDocumentModal(props: Props) {
  const { currentSpace } = useSpace();
  const { data: documentTypes } = useGetDocumentTypes();
  const { mutateAsync: updateDocument, isPending: isUpdatePending } = useUpdateDocument();
  const { mutateAsync: renameFileInDrive, isPending: isRenamePending } = useRenameFileInGoogleDrive();

  const [serverError, setServerError] = useState<string | null>(null);

  const form = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      name: props.document.name ?? '',
      type: props.document.type ?? null,
      provider_id: props.document.providerId ?? null,
      provider_name: props.document.providerName ?? '',
    },
    resolver: zodResolver(formSchema),
  });

  async function handleConfirm(formData: z.infer<typeof formSchema>) {
    setServerError(null);

    try {
      const newName = formData.name.trim();

      // Rename the underlying Drive file first so the DB row and Drive stay in
      // sync; a no-op for links and non-Drive URLs.
      if (newName !== props.document.name && currentSpace) {
        await renameFileInDrive({
          fileUrl: props.document.fileUrl,
          newName,
          spaceId: currentSpace.id,
        });
      }

      const matchingType = documentTypes?.find((dt) => dt.name === formData.type || dt.id === formData.type);

      await updateDocument({
        id: props.document.id,
        command: {
          name: newName,
          document_type_id: matchingType?.id ?? null,
          provider_id: formData.provider_id,
        },
      });

      enqueueSnackbar('Document modifié.', 'success');
      props.onClose();
    } catch (errors: unknown) {
      if (isSupabaseError(errors)) {
        setServerError(errors.message);

        return;
      }

      setServerError('Une erreur est survenue.');
    }
  }

  return {
    form,
    documentTypes: documentTypes ?? [],
    handleConfirm,
    handleSubmit: form.handleSubmit(handleConfirm),
    serverError,
    isConfirmationPending: isUpdatePending || isRenamePending,
  };
}
